import type { ReactNode } from "react";
import { ChevronRightIcon } from "./Icons.tsx";

/**
 * The list vocabulary the owner screens are written in.
 *
 * The member side is a feed of cards, because a member reads their week. The owner side is a
 * register: members, fees, packages, class types. A register wants rows of equal weight inside one
 * bounded card, so the eye runs down a column of names and values rather than across a stack of
 * separate objects. Three pieces do all of it: a `Card` to bound the group, a `SectionHeader` to
 * name it, and a `ListRow` for the rows.
 */

/** A bounded group of rows. Everything inside shares one border and one shadow. */
export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={["overflow-hidden rounded-card border border-line bg-paper shadow-card", className]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

/**
 * The rule above a group, with an optional count on the right.
 *
 * The same silhouette as the schedule's day header, minus the stickiness: an owner list is a few
 * screens long at most, so a header that follows you down is more chrome than help.
 */
export function SectionHeader({
  title,
  detail,
  sticky = false,
}: {
  title: string;
  detail?: string;
  /** For a long agenda, where which day you are looking at is the one thing worth keeping on screen. */
  sticky?: boolean;
}) {
  return (
    <div
      className={[
        "flex items-center gap-3 pt-6 pb-2.5",
        sticky ? "sticky top-(--sticky-top) z-10 -mx-5 bg-surface/95 px-5 backdrop-blur-sm" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h2 className="text-[15px] font-semibold">{title}</h2>
      <span className="h-px flex-1 bg-line" aria-hidden="true" />
      {detail ? (
        <span className="text-[11px] font-semibold tracking-[0.08em] text-ink-3 uppercase">
          {detail}
        </span>
      ) : null}
    </div>
  );
}

/**
 * One row: a title, an optional second line, an optional value on the right.
 *
 * Tappable rows are buttons and reach the 48px minimum the rest of the app holds to. Rows that only
 * state a fact are not buttons, because a control that does nothing when pressed teaches people to
 * stop trusting the ones that do.
 */
export function ListRow({
  title,
  subtitle,
  value,
  valueColor,
  leading,
  trailing,
  onSelect,
  muted = false,
  last = false,
}: {
  title: string;
  subtitle?: string;
  value?: string;
  /** A person's mark colour for the value (a `markColor`, AA on white). Defaults to ink. */
  valueColor?: string;
  /** A small mark before the title, such as a person's colour. */
  leading?: ReactNode;
  trailing?: ReactNode;
  onSelect?: () => void;
  muted?: boolean;
  last?: boolean;
}) {
  const body = (
    <>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <div className="min-w-0 flex-1">
        <p
          dir="auto"
          className={["truncate text-left text-[15px] font-semibold", muted ? "text-ink-2" : "text-ink"].join(
            " ",
          )}
        >
          {title}
        </p>
        {subtitle ? (
          <p dir="auto" className="mt-0.5 truncate text-left text-[13px] text-ink-2">
            {subtitle}
          </p>
        ) : null}
      </div>
      {value ? (
        <span className="shrink-0 text-[15px] font-semibold text-ink tabular-nums" style={valueColor ? { color: valueColor } : undefined}>
          {value}
        </span>
      ) : null}
      {trailing}
      {onSelect ? (
        <span className="shrink-0 text-ink-3">
          <ChevronRightIcon size={18} />
        </span>
      ) : null}
    </>
  );

  const shell = [
    "flex w-full min-h-12 items-center gap-3 px-4 py-3 text-left",
    last ? "" : "border-b border-line",
  ]
    .filter(Boolean)
    .join(" ");

  if (!onSelect) return <div className={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`${shell} transition-colors duration-150 active:bg-sunken`}
    >
      {body}
    </button>
  );
}

/**
 * A label and a value, for the facts inside a sheet.
 *
 * Separate from `ListRow` on purpose: these are read, never pressed, and the label is the quiet
 * half rather than the loud one.
 */
export function FactRow({
  label,
  value,
  last = false,
  typed = false,
}: {
  label: string;
  value: string;
  last?: boolean;
  /** The value is text a person typed (a note): it reads in its own direction (`dir="auto"`). */
  typed?: boolean;
}) {
  return (
    <div
      className={[
        "flex items-baseline justify-between gap-4 py-2.5",
        last ? "" : "border-b border-line",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="text-[14px] text-ink-2">{label}</span>
      <span className="text-right text-[14px] font-semibold text-ink" dir={typed ? "auto" : undefined}>
        {value}
      </span>
    </div>
  );
}
